import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { signatureKeys } from '@/lib/query-keys';
import { signatureService } from '@/services/signature.service';

export function useSignatures() {
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: signatureKeys.list(),
    queryFn: () => signatureService.list(),
    staleTime: 30_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: signatureKeys.all });

  const create = useMutation({
    mutationFn: (data: Parameters<typeof signatureService.create>[0]) => signatureService.create(data),
    onSuccess: () => { invalidate(); toast.success('Signature added'); },
    onError: (err: unknown) => toast.error(extractMessage(err) || 'Failed to add signature'),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof signatureService.update>[1] }) => signatureService.update(id, data),
    onSuccess: () => { invalidate(); toast.success('Signature updated'); },
    onError: (err: unknown) => toast.error(extractMessage(err) || 'Failed to update signature'),
  });

  const replaceFile = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => signatureService.replaceFile(id, file),
    onSuccess: () => { invalidate(); toast.success('Signature image replaced'); },
    onError: (err: unknown) => toast.error(extractMessage(err) || 'Failed to replace image'),
  });

  const setDefault = useMutation({
    mutationFn: (id: string) => signatureService.setDefault(id),
    onSuccess: () => { invalidate(); toast.success('Default signature updated'); },
    onError: (err: unknown) => toast.error(extractMessage(err) || 'Failed to set default'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => signatureService.remove(id),
    onSuccess: () => { invalidate(); toast.success('Signature deleted'); },
    onError: (err: unknown) => toast.error(extractMessage(err) || 'Failed to delete signature'),
  });

  const signatures = listQuery.data?.data ?? [];
  return {
    signatures,
    isLoading: listQuery.isLoading,
    defaultSignature: signatures.find((s) => s.isDefault) ?? null,
    create, update, replaceFile, setDefault, remove,
  };
}

function extractMessage(err: unknown): string | null {
  if (typeof err === 'object' && err !== null) {
    const e = err as { response?: { data?: { message?: string | string[] } } };
    const msg = e.response?.data?.message;
    if (typeof msg === 'string') return msg;
    if (Array.isArray(msg)) return msg.join('; ');
  }
  return null;
}
